import React from 'react';
import { useSelector } from 'react-redux';
import { firebaseAuth, firestore } from '../firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import './NotificationsScreen.css';

// Get icon for notification type/status
const getNotificationIcon = (type) => {
  const iconMap = {
    placed: '📦',
    ordered: '📦',
    packed: '📋',
    shipped: '🚚',
    delivered: '✅',
    cancelled: '❌',
    info: '🔔',
    success: '✅',
    warning: '⚠️',
    error: '❌',
    payment: '💳',
    refund: '💰',
    return: '↩️',
    exchange: '🔄',
    discount: '🏷️',
    offer: '🎉',
    reminder: '⏰',
    update: '🔄',
    welcome: '👋',
    account: '👤',
    security: '🔒',
    system: '⚙️'
  };
  const key = (type || 'info').toLowerCase();
  return iconMap[key] || iconMap.info;
};

function NotificationsScreen() {
  const userSignin = useSelector((state) => state.userSignin) || {};
  const { userInfo } = userSignin;

  const [items, setItems] = React.useState([]); // [{idx,title,body,image,type,readed,createdAt}]
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('all'); // 'all' | 'unread'

  React.useEffect(() => {
    const uid = userInfo?.uid || firebaseAuth.currentUser?.uid;
    if (!uid) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ref = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_NOTIFICATIONS');
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      const size = Number(d.list_size) || 0;
      const list = [];
      for (let i = size - 1; i >= 0; i--) {
        const title = d[`title_${i}`] ?? d[`Title_${i}`] ?? '';
        const body = d[`body_${i}`] ?? d[`Body_${i}`] ?? '';
        const image = d[`image_${i}`] ?? d[`Image_${i}`] ?? '';
        const type = d[`type_${i}`] ?? d[`Type_${i}`] ?? 'info';
        list.push({
          idx: i,
          title: title || (body ? 'Notification' : ''),
          body: body || '',
          image: image || '',
          type: type || 'info',
          readed: (d.hasOwnProperty(`readed_${i}`) ? !!d[`readed_${i}`] : (d.hasOwnProperty(`Readed_${i}`) ? !!d[`Readed_${i}`] : false)),
          createdAt: d[`createdAt_${i}`] && typeof d[`createdAt_${i}`].toDate === 'function' ? d[`createdAt_${i}`].toDate() : null,
          orderId: d[`orderId_${i}`] || '',
          productId: d[`productId_${i}`] || '',
        });
      }
      setItems(list);
      setLoading(false);
      setError('');
    }, (err) => {
      console.error('Notifications subscribe error:', err);
      setError('Failed to load notifications');
      setLoading(false);
    });
    return () => unsub();
  }, [userInfo?.uid]);

  const markAllAsRead = async () => {
    try {
      const uid = userInfo?.uid || firebaseAuth.currentUser?.uid;
      if (!uid) return;
      const ref = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_NOTIFICATIONS');
      const updates = {};
      items.forEach((it) => {
        if (!it.readed) {
          // Update both casings for compatibility with Android-written docs
          updates[`readed_${it.idx}`] = true;
          updates[`Readed_${it.idx}`] = true;
        }
      });
      if (Object.keys(updates).length > 0) {
        await updateDoc(ref, updates);
      }
      // Optimistic UI
      setItems((prev) => prev.map(it => ({ ...it, readed: true })));
    } catch (e) {
      console.warn('Mark all as read failed:', e?.message || e);
    }
  };

  const markAsRead = async (idx) => {
    try {
      const uid = userInfo?.uid || firebaseAuth.currentUser?.uid;
      if (!uid) return;
      const ref = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_NOTIFICATIONS');
      await updateDoc(ref, { [`readed_${idx}`]: true, [`Readed_${idx}`]: true });
      setItems(prev => prev.map(it => it.idx === idx ? { ...it, readed: true } : it));
    } catch (e) { 
      console.warn('Mark read failed', e?.message || e); 
    }
  };

  const filtered = React.useMemo(() => {
    if (activeTab === 'unread') return items.filter(it => !it.readed);
    return items;
  }, [items, activeTab]);

  return (
    <div className="container page page-notifications">
      <div className="notif-header">
        <div className="notif-title">Notifications</div>
        <div className="notif-actions">
          <div className="notif-tabs">
            <button className={`notif-tab ${activeTab==='all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All
              <span className="notif-tab-badge">{items.length}</span>
            </button>
            <button className={`notif-tab ${activeTab==='unread' ? 'active' : ''}`} onClick={() => setActiveTab('unread')}>Unread
              <span className="notif-tab-badge">{items.filter(i=>!i.readed).length}</span>
            </button>
          </div>
          <button className="notif-markall" onClick={markAllAsRead} disabled={items.every(it => it.readed) || items.length === 0}>
            Mark all as read
          </button>
        </div>
      </div>
      {loading ? (
        <div className="card card-body">
          <div className="loading-container" style={{ minHeight: 120 }}>
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading…</div>
          </div>
        </div>
      ) : error ? (
        <div className="card card-body">
          <div className="loading-text">{error}</div>
        </div>
      ) : items.length === 0 ? (
        <div className="card card-body notif-empty">
          <div className="notif-empty-emoji">🔔</div>
          <div className="notif-empty-title">You're all caught up</div>
          <div className="notif-empty-sub">No notifications yet.</div>
        </div>
      ) : (
        <div className="card card-body" style={{padding:0}}>
          <ul className="notif-list">
            {filtered.map((n) => (
              <li 
                key={n.idx} 
                className={`notif-item ${n.readed ? 'is-read' : 'is-unread'} notif-type-${(n.type||'info').toLowerCase()}`}
                onClick={() => !n.readed && markAsRead(n.idx)}
              >
                <div className="notif-card">
                  <div className="notif-accent" />
                  <div className="notif-avatar">
                    {n.image ? (
                      <img src={n.image} alt="icon" />
                    ) : (
                      <span className="notif-icon">{getNotificationIcon(n.type)}</span>
                    )}
                  </div>
                  <div className="notif-content">
                    <div className="notif-row">
                      <div className="notif-title-text">{n.title || 'Notification'}</div>
                      {!n.readed && <span className="notif-dot" />}
                    </div>
                    <div className="notif-body">{n.body}</div>
                    <div className="notif-meta">
                      <span className="notif-time">{n.createdAt ? n.createdAt.toLocaleString() : ''}</span>
                      {n.orderId && (
                        <>
                          <span className="notif-sep">•</span>
                          <span className="notif-order">#{n.orderId.slice(-6)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {!n.readed && (
                    <div className="notif-status">
                      <span className="notif-new">NEW</span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default NotificationsScreen;
